import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { quickAuthCheck } from '../services/unifiedAuthService';

export const useRecoveryDialog = () => {
  const { keycloak, initialized } = useKeycloak();
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  // Function to check authorization status
  const checkAuthorizationStatus = async () => {
    if (!initialized || !keycloak.authenticated) {
      return { is_authorized: false, is_admin: false };
    }

    try {
      setIsCheckingAuth(true);
      const authData = await quickAuthCheck();
      return authData;
    } catch (error) {
      console.error('Authorization check failed:', error);
      return { is_authorized: false, is_admin: false };
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // Function to show recovery dialog when user is unauthorized
  const handleUnauthorizedAccess = async (showDialogOnUnauthorized = true) => {
    const authData = await checkAuthorizationStatus();
    
    // Normalize authData: if user has all agent IDs, treat as configured
    if (authData && !authData.is_configured) {
      const hasAllAgentIds = authData.agent_ids_configured && 
        Object.values(authData.agent_ids_configured).every(configured => configured === true);
      
      if (hasAllAgentIds) {
        console.log('🔄 NORMALIZING: User has all agent IDs, setting is_configured to true');
        authData.is_configured = true;
      }
    }
    
    // Consider user authorized if they have is_authorized OR is_configured
    const isUserAuthorized = authData.is_authorized || authData.is_configured;
    
    if (!isUserAuthorized && showDialogOnUnauthorized) {
      setShowRecoveryDialog(true);
      return false; // Not authorized
    }
    
    return isUserAuthorized; // Return authorization status
  };

  // Function to close recovery dialog
  const closeRecoveryDialog = () => {
    setShowRecoveryDialog(false);
  };

  // Function to handle successful recovery
  const handleRecoverySuccess = () => {
    setShowRecoveryDialog(false);
    // Update local storage to reflect new authorization status
    localStorage.setItem('isUserAuthorized', 'true');
    sessionStorage.setItem('wasAuthorized', 'true');
  };

  // Periodic check for authorization revocation
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;

    const checkForRevocation = async () => {
      const wasAuthorized = sessionStorage.getItem('wasAuthorized') === 'true';
      const currentlyAuthorized = localStorage.getItem('isUserAuthorized') === 'true';
      
      // If user was authorized before, check current status
      if (wasAuthorized && currentlyAuthorized) {
        const authData = await checkAuthorizationStatus();
        
        // If authorization was revoked, show recovery dialog
        if (!authData.is_authorized) {
          localStorage.setItem('isUserAuthorized', 'false');
          setShowRecoveryDialog(true);
        }
      }
    };

    // Check immediately
    checkForRevocation();

    // Set up periodic checking (every 5 minutes)
    const interval = setInterval(checkForRevocation, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [initialized, keycloak.authenticated]);

  return {
    showRecoveryDialog,
    isCheckingAuth,
    checkAuthorizationStatus,
    handleUnauthorizedAccess,
    closeRecoveryDialog,
    handleRecoverySuccess
  };
};